import { useMemo } from 'react';
import type { Event, ParticipantBalance, GroupBalance } from '../types';

// Pure function to calculate participant balances
export function calculateParticipantBalances(event: Event | null): ParticipantBalance[] {
  if (!event) return [];

  const balances = new Map<string, number>();

  // Initialize all participants with 0 balance
  event.participants.forEach((p) => balances.set(p.id, 0));

  // Process each expense
  event.expenses.forEach((expense) => {
    if (expense.participantIds.length === 0) return;

    const splitAmount = expense.amount / expense.participantIds.length;

    // The payer gets credited for the full amount
    const payerBalance = balances.get(expense.paidById) || 0;
    balances.set(expense.paidById, payerBalance + expense.amount);

    // Each participant (including payer) owes their share
    expense.participantIds.forEach((participantId) => {
      const currentBalance = balances.get(participantId) || 0;
      balances.set(participantId, currentBalance - splitAmount);
    });
  });

  // Convert to array with participant info
  return event.participants.map((p) => ({
    participantId: p.id,
    participantName: p.name,
    groupId: p.groupId,
    balance: Math.round(balances.get(p.id) || 0),
  }));
}

// Pure function to calculate group balances
export function calculateGroupBalances(
  event: Event | null,
  participantBalances: ParticipantBalance[]
): GroupBalance[] {
  if (!event) return [];

  const groupBalanceMap = new Map<string, number>();

  // Sum participant balances by group
  participantBalances.forEach((pb) => {
    if (!pb.groupId) return;
    const current = groupBalanceMap.get(pb.groupId) || 0;
    groupBalanceMap.set(pb.groupId, current + pb.balance);
  });

  // Convert to array with group info
  return event.groups.map((group) => {
    const representative = event.participants.find((p) => p.id === group.representativeId);
    return {
      groupId: group.id,
      groupName: group.name,
      representativeId: group.representativeId,
      representativeName: representative?.name || 'Sin representante',
      totalBalance: Math.round(groupBalanceMap.get(group.id) || 0),
    };
  });
}

// Pure function to filter unassigned participants
export function filterUnassignedBalances(participantBalances: ParticipantBalance[]): ParticipantBalance[] {
  return participantBalances.filter((pb) => !pb.groupId);
}

// Pure function to calculate total spent
export function calculateTotalSpent(event: Event | null): number {
  if (!event) return 0;
  return event.expenses.reduce((sum, e) => sum + e.amount, 0);
}

// React hook that wraps the pure functions with memoization
export function useBalances(event: Event | null) {
  const participantBalances = useMemo(
    () => calculateParticipantBalances(event),
    [event]
  );

  const groupBalances = useMemo(
    () => calculateGroupBalances(event, participantBalances),
    [event, participantBalances]
  );

  const unassignedBalances = useMemo(
    () => filterUnassignedBalances(participantBalances),
    [participantBalances]
  );

  const totalSpent = useMemo(
    () => calculateTotalSpent(event),
    [event]
  );

  return {
    participantBalances,
    groupBalances,
    unassignedBalances,
    totalSpent,
  };
}
